import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  IconButton,
  Skeleton,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  AddRounded,
  ArrowDropDown as ArrowDropDownIcon,
  SearchRounded,
  TuneRounded,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import isoWeek from "dayjs/plugin/isoWeek";
import { useSelectedWatchlist } from "../../Watchlists/hooks/useSelectedWatchlist";
import {
  EARNINGS_STORAGE_KEY,
  EARNINGS_PREFERRED_WATCHLIST_NAME,
} from "../earningsSelection";
import { useWatchlistEarnings, WatchlistEarningsEntry } from "../../../../hooks/useWatchlistEarnings";
import { useTouchLayout } from "../../../../hooks/useTouchLayout";
import { MAX_WATCHLISTS_PER_USER } from "../../Watchlists/watchlistLimits";
import WatchlistSwitcher from "../../Watchlists/WatchlistSwitcher";
import WatchlistNameModal from "../../Watchlists/WatchlistNameModal";
import ManageWatchlistModal from "../../Watchlists/ManageWatchlistModal";
import SearchBar from "../../../common/SearchBar/SearchBar";
import EarningsSearchHero from "../EarningsSearchHero";
import EarningsRow from "../EarningsRow";
import { EarningsTab } from "../EarningsPage";
import { hapticSelection } from "../../../../lib/haptics";

// isoWeek gives Monday-start weeks; utc keeps the reporting date a calendar
// date rather than an instant that shifts with the reader's timezone.
dayjs.extend(utc);
dayjs.extend(isoWeek);

interface HomeTabProps {
  onTabChange: (tab: EarningsTab) => void;
}

const DEFAULT_SKELETON_ROWS = 5;

// Matches the 34px switcher height so the controls sit flush with it.
const iconButtonSx = (isDark: boolean, idleColor: string) => ({
  width: 34,
  height: 34,
  color: idleColor,
  transition: "color 0.18s ease, background-color 0.18s ease",
  "&:hover": {
    color: "#22c55e",
    backgroundColor: isDark ? "rgba(34,197,94,0.1)" : "rgba(34,197,94,0.05)",
  },
  "&:focus-visible": { outline: "2px solid #22c55e", outlineOffset: -2 },
});

type EarningsGroup = {
  label: string;
  /** Reporting inside the current week — the only rows worth colouring. */
  highlight: boolean;
  entries: WatchlistEarningsEntry[];
};

/**
 * Buckets by when a company reports, which is the question this screen exists
 * to answer. The server has already sorted soonest-first with unknown dates
 * last, so a single pass preserves that order inside each group.
 *
 * Real Monday-to-Sunday weeks, not rolling 7/14-day windows. A window would
 * put next Monday's report under "This week" every Friday — and since earnings
 * cluster Mon-Thu, that disagreed with the heading most weeks rather than
 * rarely.
 *
 * Comparison is on calendar dates as YYYY-MM-DD strings, never on instants.
 * The reporting date is a date, not a moment: parsing its midnight-UTC form in
 * a western timezone would roll it back a day and move companies between
 * groups depending on where the reader is sitting.
 */
const groupByHorizon = (entries: WatchlistEarningsEntry[]): EarningsGroup[] => {
  const today = dayjs();
  const thisWeekEnd = today.endOf("isoWeek").format("YYYY-MM-DD");
  const nextWeekEnd = today.add(1, "week").endOf("isoWeek").format("YYYY-MM-DD");

  const groups: EarningsGroup[] = [
    { label: "This week", highlight: true, entries: [] },
    { label: "Next week", highlight: false, entries: [] },
    { label: "Later", highlight: false, entries: [] },
    { label: "No date set", highlight: false, entries: [] },
    { label: "Couldn't load", highlight: false, entries: [] },
  ];

  entries.forEach((entry) => {
    // Kept out of "No date set": that heading asserts a fact about the
    // company, and a lookup failure isn't one. The group only appears when
    // something actually failed.
    if (entry.earningsUnavailable) {
      groups[4].entries.push(entry);
      return;
    }
    if (entry.nextEarningsDate === null) {
      groups[3].entries.push(entry);
      return;
    }

    const reportDate = dayjs.utc(entry.nextEarningsDate).format("YYYY-MM-DD");
    if (reportDate <= thisWeekEnd) {
      groups[0].entries.push(entry);
      return;
    }
    if (reportDate <= nextWeekEnd) {
      groups[1].entries.push(entry);
      return;
    }
    groups[2].entries.push(entry);
  });

  return groups.filter((group) => group.entries.length > 0);
};

const HomeTab: React.FC<HomeTabProps> = ({ onTabChange }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const navigate = useNavigate();
  const isTouch = useTouchLayout();

  const [switcherAnchor, setSwitcherAnchor] = useState<HTMLElement | null>(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isManageOpen, setIsManageOpen] = useState(false);

  const {
    watchlists,
    selectedWatchlist,
    selectedWatchlistId,
    selectWatchlist,
    isLoading: watchlistsLoading,
  } = useSelectedWatchlist({
    storageKey: EARNINGS_STORAGE_KEY,
    preferName: EARNINGS_PREFERRED_WATCHLIST_NAME,
  });

  const {
    data: earnings,
    isLoading: earningsLoading,
    error: earningsError,
    refetch,
  } = useWatchlistEarnings(selectedWatchlistId);

  const entries = earnings?.data ?? [];
  const hasNoWatchlists = !watchlistsLoading && watchlists.length === 0;
  const atWatchlistLimit = watchlists.length >= MAX_WATCHLISTS_PER_USER;
  const isLoading = watchlistsLoading || (earningsLoading && !earnings);
  // The row count is already known from the cached watchlists query, so the
  // skeleton reserves the real height and the list lands without shifting.
  const skeletonRows = Math.max(
    selectedWatchlist?.holdings_count ?? DEFAULT_SKELETON_ROWS,
    1,
  );

  const handleTickerClick = (ticker: string) => {
    hapticSelection();
    navigate(`/earnings/company/${ticker}`);
  };

  const handleDeleted = () => {
    // Fall back to the derived default rather than pointing at a dead id.
    selectWatchlist(null);
  };

  // No watchlists at all — the signup hook failed, or every list was deleted.
  if (hasNoWatchlists) {
    return <EarningsSearchHero />;
  }

  const groups = groupByHorizon(entries);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Add and manage sit directly beside the watchlist they act on, rather
          than pinned to the far edge where nothing connected them to it. On
          touch they stay in the sheet — two bare icons floating in a header
          belong to nothing visually, whichever edge they are pushed to. */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          mb: 2.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
          <Box
            component="button"
            onClick={(event: React.MouseEvent<HTMLElement>) => {
              hapticSelection();
              setSwitcherAnchor(event.currentTarget);
            }}
            aria-haspopup="menu"
            aria-expanded={switcherAnchor !== null}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              minHeight: 34,
              px: 1.5,
              // The pill reads as a box, so its BORDER aligns to the left edge
              // of the labels and rows below, not its label text. Insetting it
              // by its own padding instead made it hang 13px into the container
              // padding, which on a phone (16px) left it visibly overhanging.
              maxWidth: "100%",
              minWidth: 0,
              border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}`,
              borderRadius: 1,
              backgroundColor: isDark ? "#212121" : "#f5f5f5",
              color: theme.palette.text.primary,
              cursor: "pointer",
              font: "inherit",
              fontSize: "0.8125rem",
              fontWeight: 600,
              WebkitTapHighlightColor: "transparent",
              transition: "border-color 0.18s ease, transform 0.1s ease",
              "&:hover": { borderColor: "#22c55e" },
              "&:active": { transform: "scale(0.98)" },
              "&:focus-visible": { outline: "2px solid #22c55e", outlineOffset: 2 },
              "@media (prefers-reduced-motion: reduce)": {
                transition: "none",
                "&:active": { transform: "none" },
              },
            }}
          >
            {/* No fixed cap: the pill shrinks only when the row genuinely
                runs out of width, so a long name truncated at 180px while
                two-thirds of a phone screen sat empty beside it. The count
                and caret never shrink, so the ellipsis always eats the name
                rather than the controls. */}
            <Box
              component="span"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                minWidth: 0,
              }}
            >
              {selectedWatchlist?.name ?? "Your Watchlist"}
            </Box>
            {selectedWatchlist && (
              <Box
                component="span"
                sx={{ color: theme.palette.text.secondary, flex: "none" }}
              >
                · {selectedWatchlist.holdings_count}
              </Box>
            )}
            <ArrowDropDownIcon sx={{ fontSize: 18, flex: "none" }} />
          </Box>

          {!isTouch && (
            <Tooltip
              title={
                atWatchlistLimit
                  ? `Limit of ${MAX_WATCHLISTS_PER_USER} watchlists reached`
                  : "New watchlist"
              }
            >
              {/* Disabled buttons swallow pointer events, so the span carries the hover */}
              <span>
                <IconButton
                  size="small"
                  onClick={() => setIsCreateOpen(true)}
                  disabled={atWatchlistLimit}
                  aria-label="New watchlist"
                  sx={iconButtonSx(isDark, theme.palette.text.secondary)}
                >
                  <AddRounded sx={{ fontSize: 18 }} />
                </IconButton>
              </span>
            </Tooltip>
          )}

          {!isTouch && selectedWatchlist && (
            <Tooltip title={`Manage ${selectedWatchlist.name}`}>
              <IconButton
                size="small"
                onClick={() => setIsManageOpen(true)}
                aria-label={`Manage ${selectedWatchlist.name}`}
                sx={iconButtonSx(isDark, theme.palette.text.secondary)}
              >
                <TuneRounded sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Pointer has the width for a real search field, so finding a company
            costs one click instead of a trip to another tab. Touch keeps the
            icon — a field here would crowd the switcher off a 340px screen. */}
        {!isTouch && (
          <Box sx={{ flexShrink: 0 }}>
            <SearchBar
              placeholder="Search any company..."
              width={320}
              variant="compact"
              mode="earnings"
              height={34}
            />
          </Box>
        )}

        {isTouch && (
          <Tooltip title="Find a company">
            <IconButton
              size="small"
              onClick={() => onTabChange("search")}
              aria-label="Find a company"
              sx={{ ...iconButtonSx(isDark, theme.palette.text.secondary), flexShrink: 0 }}
            >
              <SearchRounded sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {isLoading && (
        <Box>
          {Array.from({ length: skeletonRows }).map((_, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                minHeight: 56,
                px: 1,
                mx: -1,
              }}
            >
              <Skeleton variant="rounded" width={32} height={32} sx={{ borderRadius: "5px" }} />
              <Box sx={{ flex: 1 }}>
                <Skeleton width="30%" height={14} />
                <Skeleton width="55%" height={12} />
              </Box>
              <Skeleton width={72} height={14} />
            </Box>
          ))}
        </Box>
      )}

      {!isLoading && earningsError && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => refetch()}>
              Retry
            </Button>
          }
          sx={{ borderRadius: 1.5 }}
        >
          We couldn't load this watchlist's earnings.
        </Alert>
      )}

      {!isLoading && !earningsError && entries.length === 0 && (
        <EarningsSearchHero embedded />
      )}

      {!isLoading && !earningsError && entries.length > 0 && (
        <Box>
          {groups.map((group) => (
            <Box key={group.label} sx={{ mb: 3.5 }}>
              <Typography
                sx={{
                  fontSize: "0.6875rem",
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: isDark ? "#9ca3af" : "#64748b",
                  mb: 1.25,
                }}
              >
                {group.label}
                <Box component="span" sx={{ opacity: 0.6 }}>
                  {` · ${group.entries.length}`}
                </Box>
              </Typography>

              {/* One full-width list at every size. Cards in a grid left a
                  group of one or two floating in mostly empty space; the row
                  handles the wide case internally by moving the reporting
                  block beside the company instead. */}
              {group.entries.map((entry, index) => (
                <EarningsRow
                  key={entry.id}
                  entry={entry}
                  onClick={handleTickerClick}
                  showDivider={index > 0}
                  highlight={group.highlight}
                />
              ))}
            </Box>
          ))}
        </Box>
      )}

      <WatchlistSwitcher
        open={switcherAnchor !== null}
        anchorEl={switcherAnchor}
        onClose={() => setSwitcherAnchor(null)}
        watchlists={watchlists}
        selectedWatchlistId={selectedWatchlistId}
        onSelect={selectWatchlist}
        onCreate={() => setIsCreateOpen(true)}
        onManage={selectedWatchlist ? () => setIsManageOpen(true) : undefined}
        createDisabledReason={
          atWatchlistLimit
            ? `Limit of ${MAX_WATCHLISTS_PER_USER} watchlists reached`
            : undefined
        }
        manageLabel={selectedWatchlist?.name}
      />

      <WatchlistNameModal
        open={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onCreated={(watchlist) => selectWatchlist(watchlist.id)}
      />

      <ManageWatchlistModal
        open={isManageOpen}
        onClose={() => setIsManageOpen(false)}
        watchlistId={selectedWatchlistId}
        onDeleted={handleDeleted}
      />
    </Box>
  );
};

export default HomeTab;
